/**
 * AttributionManager
 * A class to manage cross-subdomain UTM and click ID param tracking via cookies.
 */

export interface AttributionCookie {
	utm_source?: string;
	utm_medium?: string;
	utm_campaign?: string;
	utm_term?: string;
	utm_content?: string;
	gclid?: string;
	fbclid?: string;
	msclkid?: string;
	ttclid?: string;
	referrer?: string;
}

declare global {
	interface Window {
		AttributionManager?: AttributionManager;
	}
}

class AttributionManager {
	private readonly cookieDomain: string;

	// Whitelisted query parameters for attribution tracking
	private readonly allowedParams: string[] = [
		'utm_source',
		'utm_medium',
		'utm_campaign',
		'utm_term',
		'utm_content',
		'gclid',
		'fbclid',
		'msclkid',
		'ttclid',
		'referrer',
		'_gcl_au',
		'_gl',
		'_fplc',
		'_ga',
	];
	private readonly allowedParamsRegex: RegExp[] = [/_ga_(.*)/];

	/**
	 * Initializes the AttributionManager with defaults.
	 * Automatically updates the attribution cookie on instantiation.
	 * @param cookieName
	 * @param expiryDays
	 */
	constructor(
		private readonly cookieName: string = 'ideal_attribution',
		private readonly expiryDays: number = 90
	) {
		this.cookieDomain = this.getRootDomain();
		this.updateCookieFromURL();
	}

	/**
	 * Retrieves the root domain dynamically, ensuring cross-subdomain tracking.
	 * @return The root domain for setting the cookie.
	 */
	private getRootDomain(): string {
		const hostParts = window.location.hostname.split('.');
		return hostParts.length > 2
			? `.${hostParts.slice(-2).join('.')}`
			: `.${window.location.hostname}`;
	}

	/**
	 * Retrieves the referrer if it's from an external site.
	 * @return {string | null} The referrer URL if it's from an external domain, otherwise null.
	 */
	private getReferrer(): string | null {
		const referrer = document.referrer;
		if (referrer && new URL(referrer).hostname !== window.location.hostname) {
			return referrer;
		}
		return null;
	}

	private isParamAllowed(paramName: string): boolean {
		return this.allowedParams.includes(paramName) || this.isParamAllowedPerRegex(paramName);
	}

	private isParamAllowedPerRegex(paramName: string): boolean {
		return this.allowedParamsRegex.some((paramRegex) => paramRegex.test(paramName));
	}

	/**
	 * Parses the current URL's query parameters and extracts attribution values.
	 * @return {Record<string, string>} A dictionary of attribution parameters.
	 */
	private getQueryParams(): Record<string, string> {
		const params: Record<string, string> = {};
		const queryString = window.location.search.substring(1);
		const queryArray = queryString.split('&');

		let referrerSetInQuery = false;
		queryArray.forEach((param) => {
			const [key, value] = param.split('=');
			if (key && value) {
				const decodedKey = decodeURIComponent(key);
				const decodedValue = decodeURIComponent(value);

				if (this.isParamAllowed(decodedKey)) {
					params[decodedKey] = decodedValue;
					if ('referrer' === decodedKey) {
						referrerSetInQuery = true;
					}
				}
			}
		});

		// Add external referrer if applicable, and not set in query
		if (!referrerSetInQuery) {
			const referrer = this.getReferrer();
			if (referrer) {
				params.referrer = referrer;
			}
		}

		return params;
	}

	/**
	 * Retrieves a stored cookie value and parses it as JSON.
	 * @return {AttributionCookie} The parsed attribution data from the cookie.
	 */
	private getCookie(): AttributionCookie {
		const cookieString = document.cookie
			.split('; ')
			.find((row) => row.startsWith(`${this.cookieName}=`));

		return cookieString ? JSON.parse(decodeURIComponent(cookieString.split('=')[1])) : {};
	}

	/**
	 * Sets a cookie with the provided value and expiration time.
	 * @param {Record<string, string>} value - The data to store in the cookie.
	 */
	private setCookie(value: AttributionCookie): void {
		const expires = new Date();
		expires.setTime(expires.getTime() + this.expiryDays * 24 * 60 * 60 * 1000);

		document.cookie = `${this.cookieName}=${encodeURIComponent(
			JSON.stringify(value)
		)}; path=/; domain=${this.cookieDomain}; expires=${expires.toUTCString()}; Secure; SameSite=None`;

		document.body.dispatchEvent(
			new CustomEvent('AttributionManager:update', {
				detail: value,
				bubbles: true,
				cancelable: true,
			})
		);
	}

	/**
	 * Updates the attribution cookie with the latest attribution values found in the URL.
	 * If a parameter is found in the URL, it replaces the existing one in the cookie.
	 */
	private updateCookieFromURL(): void {
		const existingData = this.getCookie();
		const newParams = this.getQueryParams();
		let updated = false;

		Object.keys(newParams).forEach((key) => {
			if (
				!existingData[key as keyof AttributionCookie] ||
				existingData[key as keyof AttributionCookie] !== newParams[key]
			) {
				if (key !== 'mutinyData') {
					(existingData as Record<string, string>)[key] = newParams[key];
					updated = true;
				}
			}
		});

		if (updated) {
			this.setCookie(existingData);
		}
	}

	/**
	 * Retrieves the stored attribution parameters from the cookie.
	 * @return {AttributionCookie} The stored attribution values.
	 */
	public getData(): AttributionCookie {
		return this.getCookie();
	}

	/**
	 * Retrieves a specific attribution parameter from the cookie.
	 * @param {string} key - The attribution key to retrieve (e.g., 'utm_source').
	 * @return {string | null} The attribution value if it exists, otherwise null.
	 */
	public getDataByKey(key: string): string | null {
		const attributionData = this.getCookie();
		return attributionData[key as keyof AttributionCookie] ?? null;
	}
}

window.AttributionManager = new AttributionManager();
